<script setup lang="ts">
import {flowchartApi} from "@/container";
import {Button} from "@/core/components/ui/button";
import {ActionComponentProps, EdgeOptions, EdgePath, FlowchartDocument, PrintOutputFlowchartNode} from "@/core/interfaces/flowchart/flowchart";
import {Loader2, Printer} from "@lucide/vue";
import {computed, ref, watch} from "vue";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/core/components/ui/tooltip";
import FlowchartSelectedEdges from "@/flowcharts/Components/flowchart-modal/FlowchartSelectedEdges.vue";
import FlowchartModalBottomButton from "@/flowcharts/Components/flowchart-modal/FlowchartModalBottomButton.vue";
import {ProductRecommendation} from "@/core/interfaces/Product";
import {pluralize} from "@/core/filters/Pluralize";
import {Textarea} from "@/core/components/ui/textarea";
import useFlowchartMssante from "@/flowcharts/composables/useFlowchartMssante";

const props = defineProps<ActionComponentProps<PrintOutputFlowchartNode> & {
    path: EdgePath,
    selectedEdges: { id: number, nodeLabel: string; label: string; slug: string, options?: EdgeOptions }[],
    fieldValue: any,
    disabled: boolean,
    actionDisabled: boolean,
    loading: boolean,
    recommendations: ProductRecommendation[]
}>();
const loadingPrint = ref<boolean[]>([]);
const comment = defineModel<string>('comment', {default: ''});
const emits = defineEmits<{
    (e: 'handle-option-change', id: number, fieldSlug: string, value: string | string[]): void;
    (e: 'saveAndExit'): void;
    (e: 'print'): void;
}>();

/**
 * Handles the print action.
 */
const doPrint = async (document: FlowchartDocument, index: number) => {
    loadingPrint.value[index] = true;
    emits('print');

    const response = await flowchartApi().print(
        props.flowchart.model().id,
        props.consumer,
        printPayload(document),
        props.path
    );

    loadingPrint.value[index] = false;

    const url = window.URL.createObjectURL(new Blob([response], {type: response.type}));
    window.open(url);
};

/**
 * The print endpoint payload for a document of the node.
 */
const printPayload = (document: FlowchartDocument) => ({
    document: document.class,
    recommendations: props.recommendations?.map(r => r.id),
    comment: comment.value,
});

/**
 * Whether the document cannot be printed yet: it would come out empty without a product.
 */
const printDisabled = (document: FlowchartDocument): boolean => {
    return props.disabled || (!!document.requires_recommendations && !props.recommendations?.length);
};

/**
 * The documents to print.
 */
const payload = computed((): { documents: FlowchartDocument[] } => {
    return props.node.action_payload;
});

/** Whether one of the documents renders the pharmacist comment. */
const acceptsComment = computed((): boolean => {
    return payload.value.documents.some(document => document.accepts_comment);
});

/** The patient display name. */
const patientName = computed((): string => {
    return `${props.consumer.information.first_name} ${props.consumer.information.last_name}`;
});

/** The MSSanté message of this endpoint, with every printable document of the node. */
const {prefill: mssantePrefill, loadAttachments: loadMSSanteAttachments} = useFlowchartMssante(() => ({
    flowchart: props.flowchart.model(),
    node: props.node,
    consumer: props.consumer,
    path: props.path,
    selectedEdges: props.selectedEdges,
    documents: payload.value.documents,
    recommendationIds: props.recommendations?.map(recommendation => recommendation.id),
    comment: comment.value,
    surveyVerdict: Object.values(props.surveyVerdicts ?? {}).at(-1),
}));

watch(() => payload.value, () => {
    loadingPrint.value = payload.value.documents.map(() => false);
});

</script>

<template>
  <FlowchartSelectedEdges
    :selected-edges="selectedEdges"
    :field-value="fieldValue"
    @handle-option-change="(id, fieldSlug, value) => $emit('handle-option-change',id, fieldSlug, value)"
  />
  <div v-if="acceptsComment" class="pt-2">
    <Textarea v-model="comment" style="field-sizing: content" placeholder="Un conseil personnalisé ?" />
  </div>
  <div class="py-2">
    <TooltipProvider :delay-duration="200" :disabled="!disabled">
      <Tooltip>
        <TooltipTrigger as-child>
          <div class="flex flex-col gap-1">
            <div v-if="recommendations?.length" class="text-sm text-primary">
              Avec {{ recommendations.length }} {{ pluralize('produit conseil', recommendations.length) }}.
            </div>
            <Button
              v-for="(document, index) in payload.documents"
              :key="document.class"
              :disabled="printDisabled(document)"
              class="bg-primary"
              @click="() => doPrint(document, index)"
            >
              <Loader2 v-if="loadingPrint[index]" class="animate-spin" />
              <Printer v-else />
              {{ document.label }}
            </Button>
          </div>
        </TooltipTrigger>
        <TooltipContent class="text-sm">
          Vous devez sélectionner une des options
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  </div>
  <FlowchartModalBottomButton
    :action-disabled="actionDisabled" :loading="loading"
    :mssante-prefill="mssantePrefill"
    :mssante-attachments-loader="loadMSSanteAttachments"
    :mssante-consumer-key="consumer.consumer_key"
    :mssante-patient-name="patientName"
    @save-and-exit="$emit('saveAndExit')"
  />
</template>
